import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { UsersService, UserProfileService } from '../infrastructure/users.service';
import { QUERY_STALE_MS } from '@/shared/api/query-stale-time';
import type {
  UserDTO,
  CreateUserInput,
  SendInvitationInput,
  CurrentUserProfile,
  UpdateMyProfileInput,
  ChangePasswordInput,
} from '../infrastructure/users.service';

const usersService = new UsersService();
const profileService = new UserProfileService();

export const userKeys = {
  all: ['users'] as const,
  list: () => [...userKeys.all, 'list'] as const,
  profile: () => [...userKeys.all, 'profile'] as const,
};

export function useOrgUsers(
  cursor?: string,
  limit?: number,
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: [...userKeys.list(), { cursor, limit }],
    queryFn: () => usersService.listOrgUsers(cursor, limit),
    enabled: options?.enabled ?? true,
  });
}

export function useClinicUsers(clinicId: string, cursor?: string, limit?: number) {
  return useQuery({
    queryKey: [...userKeys.list(), 'clinic', clinicId, { cursor, limit }],
    queryFn: () => usersService.listClinicUsers(clinicId, cursor, limit),
    enabled: !!clinicId,
  });
}

export function useInviteUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: SendInvitationInput) => usersService.invite(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: userKeys.list() });
    },
  });
}

export function useCreateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateUserInput) => usersService.create(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: userKeys.list() });
    },
  });
}

export function useDeactivateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, reason }: { userId: string; reason?: string }) =>
      usersService.deactivateUser(userId, reason),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: userKeys.list() });
    },
  });
}

export function useReactivateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => usersService.reactivateUser(userId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: userKeys.list() });
    },
  });
}

export function useMyProfile(options?: { enabled?: boolean; retry?: boolean }) {
  return useQuery({
    queryKey: userKeys.profile(),
    queryFn: () => profileService.getMyProfile(),
    staleTime: QUERY_STALE_MS.CATALOG,
    enabled: options?.enabled,
    retry: options?.retry,
  });
}

export function useUpdateMyProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateMyProfileInput) => profileService.updateMyProfile(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: userKeys.profile() });
    },
  });
}

export function useChangePassword() {
  return useMutation({
    mutationFn: (input: ChangePasswordInput) => profileService.changePassword(input),
  });
}

export type {
  UserDTO,
  CreateUserInput,
  SendInvitationInput,
  CurrentUserProfile,
  UpdateMyProfileInput,
  ChangePasswordInput,
};
